import { MenuItem, MenuItemCommandEvent } from "primeng/api";
import { TerminologyUsageDeclaration } from "@app/shared/terminology/terminology-usage";
import { Deserializable } from "./deserializable.model"

/**
 * How the side rail presents an item that has children. The default unfolds the children inline
 * when the rail is expanded and shows them in a hover flyout when it is collapsed; `flyout` keeps
 * the children in a hover/focus flyout in both modes (#1530 Hearing item).
 */
export type NavItemPresentation = 'flyout';

/*
 from CAPS
 export type NavItem = {
    id?: string,
    label: string,
    link?: string,
    fragment?:string,
    replaceUrl?:boolean
    tip?: string,
    icon?: string,
    devOnly?: boolean,
    subLinks?: NavItem[],
    hasChildren?: boolean,
    expanded?:boolean,
    getFromTabsConfig?:{
      tabsKey: string
    },

    //permission?: Permission
    allowedRoles?:string[],
    itemClass?: string,
    onClickFunction?:Function

    queryParams?: Map<string, string>
  }
*/

export type NavItemConfig = {
  /* These properties come from the API. Don't change their names */
  pageCd?: string;
  displayName: string;


  /**
   * Use for internal links
   */
  route?: string;

  /**
   * Use for external links
   */
  url?: string;

  target?: '_blank' | '_self' | '_parent' | '_top';
  disabled?: boolean;
  iconClass?: string;
  iconName?: string;
  hasExtension?: boolean;
  expanded?: boolean;
  children?: NavItemConfig[] | NavItem[];
  isActive?: boolean;
  replaceUrl?: boolean;
  action?: (event?: MenuItemCommandEvent) => void;
  //isThisApp?: boolean;
  tip?: string;
  onClickFunction?: (event: MenuItemCommandEvent) => void;
  extraProps?: any;

  /** See {@link NavItemPresentation}. */
  presentation?: NavItemPresentation;

  /** Stable `data-testid` rendered on the item's control (button or link) by the side rail. */
  testId?: string;

  /**
   * Terminology usage that supplies the rendered label. `displayName` stays the usage's fallback
   * text and remains the stable `data-nav-item` key, so a vocabulary override changes what the
   * user reads without changing test hooks.
   */
  terminology?: TerminologyUsageDeclaration;

  /**
   * Route-table path used to derive access when `route` carries runtime values (for example an
   * entity id) and therefore cannot be looked up in FEATURE_ROUTES as-is, e.g.
   * `/phms/hearing-detail/:scheduledProceedingId` for `/phms/hearing-detail/42`.
   */
  accessRoute?: string;

  /**
   * Optional
   */
  [key: string]: any;
}

/**
 * NavItem
 * This represents a single menu item to be used for a Material Nav List or a PrimeNG Menu
 */
export class NavItem implements Deserializable {

  /* These properties come from the API. Don't change their names */
  displayName!: string;

  /**
   * Used for the routerLink in Angular. Use this for pages within
   * the SPA. If you need to go external, use url.
   */
  route?: string;

  /**
   * Used for the href in HTML. The default is to open in a new window or tab
   * because if you want to stay within the SPA, you should route.
   */
  url?: string;

  /**
   * Used for the href target.
   * _blank	Opens the linked document in a new window or tab
   * _self	Opens the linked document in the same frame as it was clicked (this is default)
   * _parent	Opens the linked document in the parent frame
   * _top	Opens the linked document in the full body of the window
   * <framename>	Opens the linked document in the named iframe
   *
   *  Default is _blank because most likely, if you want to replace the existing screen (_self)
   *  Then you should probably be using route instead of url. Unless you are switching between
   *  bits2 and bits3, then _self would make sense.
   */
  urlTarget?: '_blank' | '_self' | '_parent' | '_top' = '_blank';

  disabled?: boolean;

  iconClass?: string;
  /**
   * Used for the icon. Can be a material or PNG icon name - The menu will figure it out?
   */
  iconName?: string;

  hasExtension?: boolean;
  expanded?: boolean;
  children?: NavItem[];
  isActive?: boolean;
  action?: (event?: MenuItemCommandEvent) => void;  // Usually a function
  isThisApp: boolean = false;

  tip?: string;
  onClickFunction?: (event: MenuItemCommandEvent) => void;

  /** See {@link NavItemPresentation}. */
  presentation?: NavItemPresentation;

  /** Stable `data-testid` rendered on the item's control (button or link) by the side rail. */
  testId?: string;

  /** Terminology usage that supplies the rendered label; see {@link NavItemConfig.terminology}. */
  terminology?: TerminologyUsageDeclaration;

  /** Route-table path used to derive access; see {@link NavItemConfig.accessRoute}. */
  accessRoute?: string;

  /**
   * Extra properties that can be used for anything
   */
  extraProps?: any = {};

  /**
   * Optional
   */
  [key: string]: any;


  deserialize(input: NavItemConfig | NavItem): this {
    Object.assign(this, input);

    if (!this.hasChildren) {
      return this;
    }

    this.children = this.children?.map((child) => new NavItem(child)); //.deserialize(child));

    return this;
  }

  // constructor that desrializes the input
  constructor(input: NavItemConfig | NavItem) {
    this.deserialize(input);
  }

  // Aliases to make it work like a Png MenuItem without actually converting it
  get label():string {return this.displayName}

  //get iconClass(): string { return this.iconClass || 'p-menuitem-icon' }

  get icon(): string { return this.iconName || ''}

  get items(): NavItem[] { return this.children || []}

  /**
   * toPngMenuItem
   * This will recursively convert a NavItem to a PrimeNG MenuItem
   * @returns MenuItem
   */
  public toPngMenuItem(): MenuItem {

    const rtn = {
      label: this.displayName,
      tip: this.tip,
      iconClass: this.iconClass || 'p-menuitem-icon',
      icon: this.iconName,
      url: !this.hasChildren ? this.url : undefined,  // If it's blank, make it undefined, otherwise non-links will go to the homepage
      route: this.route,
      disabled: this.disabled,
      expanded: this.expanded,
      action: this.action,
      command: (event: MenuItemCommandEvent) => {
        if (this.onClickFunction) {
          this.onClickFunction(event);
        }
      },

      items: this.children?.map((childItem) => {
        return new NavItem(childItem).toPngMenuItem();
      }),
    };

    // Add a class for automatically displaying an external link icon
    if(!this.onClickFunction && !this.hasChildren)
    {
      this.extraProps.styleClass = 'auto-external-icon';
    }

    // Include extra properties if they exist
    return {...rtn, ...this.extraProps, ...this};

  }

  public get hasChildren(): boolean {
    return !!this.children && !!this.children.find((child) => !!child.hasChildren || child.route !== "/..#");
  }

  private _baseHref?: string;
  public get baseHref(): string {
    return this._baseHref || "";
  }

  public set baseHref(baseHref: string) {
    this.clearActivatedRouteState();
    this._baseHref = baseHref;

    if (!this.hasChildren) {
      return;
    }

    if(this.children)
    {
      for (const child of this.children) {
        child.baseHref = baseHref;
      }
    }
  }

  private _activatedRoute?: string;
  public get activatedRoute(): string {
    return this._activatedRoute || "";
  }
  public set activatedRoute(activatedRoute: string) {
    this.clearActivatedRouteState();
    this._activatedRoute = activatedRoute;

    if (!this.hasChildren) {
      return;
    }

    if(this.children)
    {
      for (const child of this.children) {
        child.activatedRoute = activatedRoute;
      }
    }
  }

  private clearActivatedRouteState() {
    this._isAlongActivatedRoute = undefined;
    this._hasChildrenAlongActivatedRoute = undefined;
    this._isAtEndOfActivatedRoute = undefined;
  }

  private _isAtEndOfActivatedRoute?: boolean;
  public get isAtEndOfActivatedRoute(): boolean {
    if (this._isAtEndOfActivatedRoute != undefined) {
      return this._isAtEndOfActivatedRoute;
    }

    return (this._isAtEndOfActivatedRoute = this.isAlongActivatedRoute && !this.hasChildrenAlongActivatedRoute);
  }

  private _hasChildrenAlongActivatedRoute?: boolean;
  public get hasChildrenAlongActivatedRoute(): boolean {
    if (this._hasChildrenAlongActivatedRoute != undefined) {
      return this._hasChildrenAlongActivatedRoute;
    }

    if (!this.hasChildren) {
      return (this._hasChildrenAlongActivatedRoute = false);
    }
    if(this.children)
    {
      for (const child of this.children) {
        if (child.isAlongActivatedRoute) {
          return (this._hasChildrenAlongActivatedRoute = true);
        }
      }
    }

    this._hasChildrenAlongActivatedRoute = false;
    return false;
  }

  private _isAlongActivatedRoute?: boolean;
  public get isAlongActivatedRoute(): boolean {
    if (this._isAlongActivatedRoute != undefined) {
      return this._isAlongActivatedRoute;
    }

    if (this.route?.startsWith(this.baseHref)) {
      const route = this.route.substr(this.baseHref.length);
      const activatedRoute = this.activatedRoute;
      if (activatedRoute.startsWith(route)) {
        this._isAlongActivatedRoute = true;
        return true;
      }
    }

    if (!this.hasChildren) {
      return (this._isAlongActivatedRoute = false);
    }
    if(this.children)
    {
      for (const child of this.children) {
        if (child.isAlongActivatedRoute) {
          this._isAlongActivatedRoute = true;
          return true;
        }
      }
    }

    this._isAlongActivatedRoute = false;
    return false;
  }
}
